import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Breadcrumbs,
  Button,
  ButtonGroup,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import TablePaginationWithPages from 'renderer/shared/components/TablePaginationWithPages';
import { useAppDispatch, useAppSelector } from 'renderer/shared/state/hooks';
import { formatCurrency } from 'renderer/shared/formatters/formatters';
import { useListTransactionsQuery } from '../services/transactions.endpoints';
import { selectTransactionQuery } from '../state/transaction.selectors';
import {
  setTransactionQuery,
  TransactionQueryParams,
} from '../state/transactions.slice';
import RefundDialog from './RefundDialog';
import { BaseTransaction } from '../schemas/transaction.schemas';

interface EnhancedTableToolbarProps {
  onSearchChange: (value: string) => void;
  onSearch: () => void;
  onReset: () => void;
  sortBy: string;
  setSortBy: (value: string) => void;
  sortOrder: 'asc' | 'desc';
  setSortOrder: () => void;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  onStartDateChange: (value: Dayjs | null) => void;
  onEndDateChange: (value: Dayjs | null) => void;
  onApplyDateFilter: () => void;
  onClearDateFilter: () => void;
  hasActiveDateFilter: boolean;
}

const dateFieldSlotProps = {
  textField: { size: 'small' as const, sx: { minWidth: 160 } },
};

const EnhancedTableToolbar: React.FC<EnhancedTableToolbarProps> = ({
  onSearchChange,
  onSearch,
  onReset,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onApplyDateFilter,
  onClearDateFilter,
  hasActiveDateFilter,
}) => {
  return (
    <Stack spacing={1.5} sx={{ px: 2, pt: 2, pb: 1 }}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        alignItems={{ md: 'center' }}
        useFlexGap
        flexWrap="wrap"
      >
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ minWidth: 88 }}
        >
          Purchase date
        </Typography>
        <DatePicker
          label="From"
          value={startDate}
          onChange={onStartDateChange}
          maxDate={endDate ?? undefined}
          slotProps={dateFieldSlotProps}
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ display: { xs: 'none', sm: 'block' } }}
        >
          –
        </Typography>
        <DatePicker
          label="To"
          value={endDate}
          onChange={onEndDateChange}
          minDate={startDate ?? undefined}
          slotProps={dateFieldSlotProps}
        />
        <Button variant="contained" onClick={onApplyDateFilter} size="small">
          Apply dates
        </Button>
        {hasActiveDateFilter ? (
          <Button variant="text" onClick={onClearDateFilter} size="small">
            Clear dates
          </Button>
        ) : null}
      </Stack>

      <Toolbar
        disableGutters
        sx={{
          justifyContent: 'flex-start',
          padding: 0,
          borderBottom: '1px solid rgba(224, 224, 224, 1)',
          backgroundColor: 'white',
          minHeight: 'auto',
        }}
      >
        <TextField
          variant="outlined"
          placeholder="Search"
          size="small"
          sx={{
            flexGrow: 1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '4px',
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: 'primary.main',
              },
            },
          }}
          // value={search}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            onChange: (event) => onSearchChange(event.target.value),
          }}
        />
        <Button variant="outlined" onClick={onSearch} sx={{ mx: 1 }}>
          Search
        </Button>
        <Button variant="outlined" onClick={onReset}>
          Reset
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          sx={{
            minWidth: 120,
            '& .MuiSelect-select': {
              padding: '8px',
              borderRadius: '4px',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(0, 0, 0, 0.23)',
            },
            marginRight: '16px',
          }}
        >
          <MenuItem value="firstName">First Name</MenuItem>
          <MenuItem value="lastName">Last Name</MenuItem>
          <MenuItem value="email">Email</MenuItem>
          <MenuItem value="createdAt">Created Date</MenuItem>
        </Select>
        <ButtonGroup sx={{ marginRight: 4 }}>
          <Button
            variant={sortOrder === 'asc' ? 'contained' : 'outlined'}
            onClick={() => setSortOrder()}
            size="small"
          >
            Asc
          </Button>
          <Button
            variant={sortOrder === 'desc' ? 'contained' : 'outlined'}
            onClick={() => setSortOrder()}
            size="small"
          >
            Desc
          </Button>
        </ButtonGroup>
      </Toolbar>
    </Stack>
  );
};

const TransactionList = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const query = useAppSelector(selectTransactionQuery);
  const dispatch = useAppDispatch();
  const setQuery = (q: TransactionQueryParams) =>
    dispatch(setTransactionQuery(q));
  const [draftStartDate, setDraftStartDate] = useState<Dayjs | null>(
    query.startDate ? dayjs(query.startDate) : null,
  );
  const [draftEndDate, setDraftEndDate] = useState<Dayjs | null>(
    query.endDate ? dayjs(query.endDate) : null,
  );

  const {
    data: transactions,
    isLoading,
    isFetching,
    isError,
    error,
  } = useListTransactionsQuery(query, {
    refetchOnMountOrArgChange: true,
    keepPreviousData: true,
  });

  const [refundModalOpen, setRefundModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<BaseTransaction | null>(null);

  const handleSearch = () => {
    setQuery({ ...query, search: searchQuery, page: 1 });
  };

  const handleReset = () => {
    setSearchQuery('');
    setDraftStartDate(null);
    setDraftEndDate(null);
    setQuery({
      ...query,
      search: '',
      startDate: undefined,
      endDate: undefined,
      page: 1,
    });
  };

  const handleApplyDateFilter = () => {
    setQuery({
      ...query,
      startDate: draftStartDate
        ? draftStartDate.startOf('day').toISOString()
        : undefined,
      endDate: draftEndDate
        ? draftEndDate.endOf('day').toISOString()
        : undefined,
      page: 1,
    });
  };

  const handleClearDateFilter = () => {
    setDraftStartDate(null);
    setDraftEndDate(null);
    setQuery({
      ...query,
      startDate: undefined,
      endDate: undefined,
      page: 1,
    });
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setQuery({ ...query, page: newPage + 1 });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => {
    setQuery({ ...query, limit: parseInt(event.target.value, 10), page: 1 });
  };

  const setSortBy = (sortBy: string) => {
    setQuery({ ...query, sortBy });
  };

  const setSortOrder = () => {
    setQuery({
      ...query,
      sortOrder: query.sortOrder === 'asc' ? 'desc' : 'asc',
    });
  };

  const avatarStyle = () => ({
    bgcolor: '#0426ae',
    boxShadow: '0 2px 4px 0 rgba(0,0,0,0.12)',
  });

  const getInitials = (firstName: string, lastName: string) => {
    const firstInitial = firstName ? firstName[0].toUpperCase() : '';
    const lastInitial = lastName ? lastName[0].toUpperCase() : '';
    return `${firstInitial}${lastInitial}`;
  };

  const formatDateAndTime = (dateString: string) => {
    const date = moment(dateString);
    return {
      formattedDate: date.format('MMM Do YYYY'),
      formattedTime: date.format('h:mm A'),
    };
  };

  if (isLoading && transactions === undefined) {
    return <div>Loading...</div>;
  }

  if (isError && transactions === undefined) {
    return (
      <div>
        Error:{' '}
        {error && 'status' in error
          ? String(error.status)
          : 'An error occurred'}
      </div>
    );
  }

  return (
    <Stack spacing={1} sx={{ pt: 0, pr: 3, pb: 3, pl: 3 }}>
      <Box>
        {isError ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Could not load this page. Showing the previous results.
          </Alert>
        ) : null}
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
          <MuiLink underline="hover" color="inherit" href="/#">
            Home
          </MuiLink>
          <Typography color="text.primary">Transactions</Typography>
        </Breadcrumbs>

        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Transactions
        </Typography>

        <Paper
          sx={{
            width: '100%',
            mb: 2,
            overflow: 'hidden',
            position: 'relative',
            opacity: isFetching ? 0.6 : 1,
            transition: 'opacity 0.15s ease-in-out',
          }}
        >
          {isFetching ? (
            <CircularProgress
              size={28}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                zIndex: 1,
              }}
            />
          ) : null}
          <EnhancedTableToolbar
            onSearchChange={handleSearchChange}
            onSearch={handleSearch}
            onReset={handleReset}
            sortBy={query.sortBy}
            setSortBy={setSortBy}
            sortOrder={query.sortOrder}
            setSortOrder={setSortOrder}
            startDate={draftStartDate}
            endDate={draftEndDate}
            onStartDateChange={setDraftStartDate}
            onEndDateChange={setDraftEndDate}
            onApplyDateFilter={handleApplyDateFilter}
            onClearDateFilter={handleClearDateFilter}
            hasActiveDateFilter={Boolean(query.startDate || query.endDate)}
          />
          <TableContainer>
            <Table
              sx={{
                '& .MuiTableCell-head': {
                  backgroundColor: 'rgba(224, 224, 224, 1)',
                  color: 'black',
                  fontWeight: 'bold',
                },
                '& .MuiTableCell-body': {
                  fontSize: '0.875rem',
                  '& .MuiAvatar-root': {
                    verticalAlign: 'middle',
                    marginRight: '8px',
                  },
                },
                '& .MuiIconButton-root': {
                  padding: '8px',
                },
                '& .MuiTableRow-root:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.07)',
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>User Info</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!transactions || transactions.totalDocs === 0 ? (
                  <TableRow>
                    <TableCell>
                      {' '}
                      <div>No transactions found</div>
                    </TableCell>
                  </TableRow>
                ) : (
                  transactions.docs.map(
                    (
                      {
                        _id: id,
                        productName,
                        category,
                        amount,
                        refundedAmount,
                        status,
                        email,
                        firstName,
                        lastName,
                        createdAt,
                        userId,
                      },
                      i,
                    ) => (
                      <TableRow key={id}>
                        <TableCell>
                          <Link
                            to={`/users/${userId}`}
                            style={{ textDecoration: 'none', color: 'inherit' }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                cursor: 'pointer',
                              }}
                            >
                              <Avatar sx={avatarStyle()}>
                                {getInitials(firstName || '', lastName || '')}
                              </Avatar>
                              <Box>
                                <Typography variant="subtitle2" noWrap>
                                  {`${firstName} ${lastName}`}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ fontSize: '0.8rem' }}
                                >
                                  {email}
                                </Typography>
                              </Box>
                            </Box>
                          </Link>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            <Box>
                              <Typography variant="subtitle2" noWrap>
                                {productName}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                sx={{ fontSize: '0.8rem' }}
                              >
                                {category}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              component="span"
                            >
                              {formatDateAndTime(createdAt).formattedDate}
                            </Typography>
                            <br />
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              component="span"
                            >
                              {formatDateAndTime(createdAt).formattedTime}
                            </Typography>
                          </Box>
                        </TableCell>
                        {refundedAmount > 0 ? (
                          <Tooltip
                            title={`Refunded ${formatCurrency(refundedAmount)}`}
                            // placement="right-start"
                          >
                            <TableCell sx={{ color: '#d32f2f' }}>
                              {formatCurrency(amount)}
                            </TableCell>
                          </Tooltip>
                        ) : (
                          <TableCell>{formatCurrency(amount)}</TableCell>
                        )}
                        <TableCell>{status}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Process Refund">
                            <IconButton
                              color="error"
                              onClick={() => {
                                setSelectedTransaction(transactions.docs[i]);
                                setRefundModalOpen(true);
                              }}
                            >
                              <MoneyOffIcon />
                            </IconButton>
                          </Tooltip>
                          <Link to={`/transactions/${id}`}>
                            <IconButton color="info">
                              <VisibilityIcon />
                            </IconButton>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ),
                  )
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <RefundDialog
            open={refundModalOpen}
            setOpen={setRefundModalOpen}
            transaction={selectedTransaction}
          />
          <TablePaginationWithPages
            component="div"
            count={transactions?.totalDocs || 0}
            page={query.page - 1}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPage={query.limit}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Paper>
      </Box>
    </Stack>
  );
};

export default TransactionList;
